import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, Mail, Loader2, ShoppingCart, Truck, RotateCcw, Boxes, Users, Store } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { notificationsService } from "@/services/notificationsService";
import { formatApiErrorDetail } from "@/services/api";

const CATEGORY_LABELS = {
  orders: { label: "Orders", desc: "Order created, shipped, quantity problem.", icon: ShoppingCart },
  inbound: { label: "Inbound", desc: "Shipments arriving into the warehouse.", icon: Truck },
  returns: { label: "Returns", desc: "New returns and status updates.", icon: RotateCcw },
  inventory: { label: "Inventory", desc: "Low stock and out-of-stock alerts.", icon: Boxes },
  users: { label: "Team", desc: "Invitations sent, invitations accepted.", icon: Users },
  stores: { label: "Stores", desc: "Store assignments and status changes.", icon: Store },
};

export function NotificationPreferencesCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["notification-prefs"],
    queryFn: notificationsService.getPrefs,
  });
  const [local, setLocal] = useState({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data?.prefs) {
      setLocal(data.prefs);
      setDirty(false);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (prefs) => notificationsService.updatePrefs(prefs),
    onSuccess: () => {
      toast.success("Notification preferences saved");
      qc.invalidateQueries({ queryKey: ["notification-prefs"] });
      setDirty(false);
    },
    onError: (err) => {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    },
  });

  const toggle = (cat, kind) => (value) => {
    setLocal((p) => ({
      ...p,
      [cat]: { ...(p[cat] || { inapp: true, email: false }), [kind]: !!value },
    }));
    setDirty(true);
  };

  const cats = data?.categories || Object.keys(CATEGORY_LABELS);

  return (
    <section
      data-testid="notification-prefs-card"
      className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-premium backdrop-blur-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="overline">Preferences</p>
          <h2 className="font-display mt-1 text-xl font-semibold tracking-tight text-foreground">
            Notification Preferences
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick which events reach you in the app and which also send an email.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => mutation.mutate(local)}
          disabled={!dirty || mutation.isPending}
          data-testid="notification-prefs-save"
        >
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </div>

      {isLoading ? (
        <div className="mt-6 h-20 animate-pulse rounded-xl bg-muted/50" />
      ) : (
        <div className="mt-6 overflow-hidden rounded-xl border border-border/60">
          <div className="grid grid-cols-[1fr,80px,80px] items-center gap-3 border-b border-border/60 bg-muted/40 px-4 py-2.5">
            <span className="overline">Category</span>
            <span className="overline flex items-center gap-1 justify-center"><Bell className="h-3 w-3" /> In-app</span>
            <span className="overline flex items-center gap-1 justify-center"><Mail className="h-3 w-3" /> Email</span>
          </div>
          {cats.map((cat) => {
            const meta = CATEGORY_LABELS[cat] || { label: cat, desc: "", icon: Bell };
            const Icon = meta.icon;
            const entry = local[cat] || { inapp: true, email: false };
            return (
              <div
                key={cat}
                data-testid={`notification-prefs-row-${cat}`}
                className="grid grid-cols-[1fr,80px,80px] items-center gap-3 border-t border-border/60 px-4 py-3 first:border-t-0"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{meta.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{meta.desc}</p>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={!!entry.inapp}
                    onCheckedChange={toggle(cat, "inapp")}
                    data-testid={`notification-prefs-${cat}-inapp`}
                  />
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={!!entry.email}
                    onCheckedChange={toggle(cat, "email")}
                    data-testid={`notification-prefs-${cat}-email`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
